import { useEffect } from 'react';
import { C } from '../constants';
import Glass from '../components/Glass';
import { ShieldCheck } from 'lucide-react';
import { setMeta } from '../utils/meta';

const accent = C.cyan;

const SECTIONS = [
  {
    heading: 'The Short Version',
    body: "We don't collect personal data. We don't track you. We don't know who you are. Your vote is anonymous. This applies to every state on our platform — Tamil Nadu, West Bengal, and all future states we add.",
  },
  {
    heading: 'Information We Do NOT Collect',
    bullets: [
      'Names, email addresses, or phone numbers',
      'Location data or IP addresses for identification purposes',
      'Browser fingerprints or device identifiers tied to your identity',
      'Caste, religion, or any demographic information',
      'Any information that could be used to identify you personally',
    ],
  },
  {
    heading: 'Information That Is Stored',
    body: 'Your vote choice is stored locally on your own device (using your browser\'s local storage) solely to prevent duplicate voting. This data never leaves your device in a way that identifies you. Aggregate vote counts are stored on our servers — for example, "District X has received 50 votes for Party A and 30 votes for Party B." We do not know who cast those votes.',
  },
  {
    heading: 'Cookies and Tracking',
    body: 'We use minimal cookies solely for the functioning of the site (such as preventing duplicate votes). We do not use tracking cookies, advertising pixels, or analytics tools that identify individual users. If we implement advertising in the future, those services may use their own cookies to serve relevant ads. We will update this section if and when advertising is added.',
  },
  {
    heading: 'Third-Party Services',
    body: 'The site may use third-party services for hosting, content delivery, or (in the future) advertising. Hosting provider: standard server logs may record IP addresses for security purposes — these logs are not used by us for identification and are regularly purged. Google AdSense (if/when implemented): Google may use cookies to serve ads based on your browsing history. You can opt out of personalised advertising at Google\'s Ad Settings.',
  },
  {
    heading: 'Data Security',
    body: "Since we don't collect personal data, there is minimal risk of a data breach affecting your personal information. Aggregate vote data is stored securely and is publicly visible on the site by design.",
  },
  {
    heading: 'Data for Multiple States',
    body: 'As we expand to more Indian states, the same privacy principles apply uniformly. Whether you vote in Tamil Nadu, West Bengal, Uttar Pradesh, or any other state we add, your anonymity and privacy protections are identical.',
  },
  {
    heading: "Children's Privacy",
    body: 'Our site is not directed at children under 13. We do not knowingly collect any information from children.',
  },
  {
    heading: 'Changes to This Policy',
    body: 'We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date.',
  },
];

export default function PrivacyScreen() {
  useEffect(() => {
    setMeta({
      title:       'Privacy Policy | UnofficialElections',
      description: "UnofficialElections privacy policy. We don't collect personal data. Your vote is anonymous. Read our full privacy commitment.",
      ogImage:     '/og-home.png',
      canonical:   '/privacy',
    });
  }, []);
  return (
    <main className="px-5 md:px-10 py-10 max-w-4xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        Legal
      </div>
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em', marginTop: '0.4rem', color: C.white,
      }}>
        Privacy Policy
      </h1>
      <p style={{ color: C.textDim, fontSize: '0.8rem', marginTop: '0.4rem' }}>Last updated: May 2026</p>

      {/* Hero badge */}
      <Glass style={{ padding: '1.1rem 1.3rem', marginTop: '2rem', border: `1px solid ${accent}33` }}>
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} color={accent} style={{ marginTop: 2, flexShrink: 0 }} />
          <p style={{ fontSize: '0.98rem', color: C.white, margin: 0, lineHeight: 1.65, fontWeight: 500 }}>
            We don't collect personal data. We don't track you. We don't know who you are. Your vote is 100% anonymous.
          </p>
        </div>
      </Glass>

      <p style={{ fontSize: '0.95rem', color: C.textDim, lineHeight: 1.7, marginTop: '1.5rem', maxWidth: 680 }}>
        UnofficialElections.com is committed to protecting your privacy. This policy explains what information we collect — and more importantly, what we don't — when you use our platform.
      </p>

      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '2.5rem', marginBottom: '2.5rem' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {SECTIONS.map((sec, i) => (
          <div key={i}>
            <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
              {sec.heading}
            </h2>
            {sec.body && (
              <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, margin: 0 }}>
                {sec.body}
              </p>
            )}
            {sec.bullets && (
              <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {sec.bullets.map((b, j) => (
                  <li key={j} style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.65 }}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <Glass style={{ marginTop: '3rem', padding: '1rem 1.3rem' }}>
        <p style={{ fontSize: '0.85rem', color: C.textDim, margin: 0, lineHeight: 1.6 }}>
          © unofficialelections.com · Unofficial, non-political, entertainment only.
        </p>
      </Glass>
    </main>
  );
}
